// client/src/utils/pokerStats/derived.ts
//
// Convierte counters raw (HistoryStats) en porcentajes derivados (DerivedStats).
// Ésta es la única función que debería conocer cómo calcular cada porcentaje.

import {
  HistoryStats,
  DerivedStats,
  Position,
  MatchupKey,
  MatchupDerived,
  MatchupStats,
  RfiDerived,
  OpenPosition,
  RfiPositionStats,
  PushFoldStats,
  PushFoldDerived,
  CbetTexturePosKey,
  CbetTextureStats,
  CbetTextureDerived,
  CbetTextureMultiKey,
  CbetTextureMultiDerived,
  FlopTexture,
  IpOopType,
  OPEN_POSITIONS,
  VpipPositionStats,
  VpipDerived,
  FoldToPushStats,
  FoldToPushDerived,
  ShoverStackBin,
  SHOVER_STACK_BINS,
} from './types';
import { MIN_MATCHUP_SAMPLE, MIN_RFI_SAMPLE, MIN_PUSHFOLD_SAMPLE, MIN_TEXTURE_SAMPLE, MIN_VPIP_SAMPLE, MIN_FOLD_TO_PUSH_SAMPLE } from './constants';

// Helper: porcentaje entero redondeado
const pct = (n: number, d: number): number => (d === 0 ? 0 : Math.round((n / d) * 100));

// ─────────────────────────────────────────────────────────────────────────────
// Parsea una MatchupKey back a sus componentes (heroPos, villainPos).
// Retorna null para 'multi' o claves inválidas.
// ─────────────────────────────────────────────────────────────────────────────
export function parseMatchupKey(key: MatchupKey): { hero: Position; villain: Position } | null {
  if (key === 'multi') return null;
  const parts = key.split('_vs_');
  if (parts.length !== 2) return null;
  return { hero: parts[0] as Position, villain: parts[1] as Position };
}

// ─────────────────────────────────────────────────────────────────────────────
// Agrega los matchups de cbet flop en un array ordenado por sample size
// ─────────────────────────────────────────────────────────────────────────────
function buildMatchupArray(
  matchups: Partial<Record<MatchupKey, MatchupStats>> | undefined
): MatchupDerived[] {
  if (!matchups) return [];

  const result: MatchupDerived[] = [];

  for (const key of Object.keys(matchups) as MatchupKey[]) {
    const bucket = matchups[key];
    if (!bucket || bucket.opp === 0) continue;

    const parsed = parseMatchupKey(key);

    result.push({
      key,
      heroPos: parsed ? parsed.hero : null,
      villainPos: parsed ? parsed.villain : null,
      opp: bucket.opp,
      count: bucket.count,
      success: bucket.success,
      cbetPct: pct(bucket.count, bucket.opp),
      successPct: pct(bucket.success, bucket.count),
      lowSample: bucket.opp < MIN_MATCHUP_SAMPLE,
    });
  }

  // Ordenar por opp descendente (más data primero)
  result.sort((a, b) => b.opp - a.opp);
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase C: construye el array de Fold to Push por stack bin del shover.
// Etiquetas legibles para cada bin (usadas directo en UI).
// ─────────────────────────────────────────────────────────────────────────────
const SHOVER_BIN_LABELS: Record<ShoverStackBin, string> = {
  micro: '≤10bb',
  short: '10–20bb',
  mid:   '20–30bb',
  deep:  '>30bb',
};

function buildFoldToPushArray(
  byStack: Partial<Record<ShoverStackBin, FoldToPushStats>> | undefined
): FoldToPushDerived[] {
  return SHOVER_STACK_BINS.map(bin => {
    const bucket = byStack?.[bin];
    const opp   = bucket?.opp   || 0;
    const folds = bucket?.folds || 0;
    return {
      bin,
      label: SHOVER_BIN_LABELS[bin],
      opp,
      folds,
      foldPct: pct(folds, opp),
      lowSample: opp < MIN_FOLD_TO_PUSH_SAMPLE,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Construye el array de VPIP por posición ordenado canónicamente
// (EP → HJ → CO → BTN → SB → BB). Siempre emite las 6 posiciones.
// ─────────────────────────────────────────────────────────────────────────────
const VPIP_POSITIONS: Position[] = ['EP', 'HJ', 'CO', 'BTN', 'SB', 'BB'];

function buildVpipArray(
  vpipByPosition: Partial<Record<Position, VpipPositionStats>> | undefined
): VpipDerived[] {
  const result: VpipDerived[] = [];
  for (const pos of VPIP_POSITIONS) {
    const bucket = vpipByPosition?.[pos];
    const opp = bucket?.opp || 0;
    const count = bucket?.count || 0;
    result.push({
      position: pos,
      opp,
      count,
      pct: pct(count, opp),
      lowSample: opp < MIN_VPIP_SAMPLE,
    });
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Construye el array de RFI por posición ordenado canónicamente (EP→BTN→SB).
// Siempre incluye TODAS las posiciones aunque tengan opp=0, así la UI puede
// renderizar la grilla completa con N/A en las que faltan datos.
// ─────────────────────────────────────────────────────────────────────────────
function buildRfiArray(
  rfiByPosition: Partial<Record<OpenPosition, RfiPositionStats>> | undefined
): RfiDerived[] {
  const result: RfiDerived[] = [];
  for (const pos of OPEN_POSITIONS) {
    const bucket = rfiByPosition?.[pos];
    const opp   = bucket?.opp   || 0;
    const count = bucket?.count || 0;
    const limps = bucket?.limps || 0;
    result.push({
      position: pos,
      opp,
      count,
      limps,
      pct:     pct(count, opp),
      limpPct: pct(limps, opp),
      lowSample: opp < MIN_RFI_SAMPLE,
    });
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase A: construye el array de Push/Fold por posición.
// Mismo patrón que buildRfiArray — siempre incluye todas las posiciones.
// ─────────────────────────────────────────────────────────────────────────────
function buildPushFoldArray(
  pushFoldByPosition: Partial<Record<OpenPosition, PushFoldStats>> | undefined
): PushFoldDerived[] {
  const result: PushFoldDerived[] = [];
  for (const pos of OPEN_POSITIONS) {
    const bucket = pushFoldByPosition?.[pos];
    const opp = bucket?.opp || 0;
    const pushes = bucket?.pushes || 0;
    const limps = bucket?.limps || 0;
    const minRaises = bucket?.minRaises || 0;
    const folds = bucket?.folds || 0;
    result.push({
      position: pos,
      opp,
      pushes,
      limps,
      minRaises,
      folds,
      pushPct: pct(pushes, opp),
      limpPct: pct(limps, opp),
      minRaisePct: pct(minRaises, opp),
      foldPct: pct(folds, opp),
      lowSample: opp < MIN_PUSHFOLD_SAMPLE,
    });
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase B: construye el array de CBet Flop por textura × posición.
// Siempre emite los 10 buckets (5 textures × 2 pos) incluso con opp=0, para
// que la UI pueda renderizar la grilla completa con N/A en los faltantes.
// Orden: por textura canónica (dry → semi-wet → wet → paired → monotone)
// y dentro de cada una, IP primero luego OOP.
// ─────────────────────────────────────────────────────────────────────────────
const TEXTURE_ORDER: FlopTexture[] = ['dry', 'semi-wet', 'wet', 'paired', 'monotone'];
const IPOOP_ORDER: IpOopType[] = ['ip', 'oop'];

function buildCbetTextureArray(
  cbetByTexture: Partial<Record<CbetTexturePosKey, CbetTextureStats>> | undefined
): CbetTextureDerived[] {
  const result: CbetTextureDerived[] = [];
  for (const texture of TEXTURE_ORDER) {
    for (const posType of IPOOP_ORDER) {
      const key = `${texture}-${posType}` as CbetTexturePosKey;
      const bucket = cbetByTexture?.[key];
      const opp = bucket?.opp || 0;
      const count = bucket?.count || 0;
      result.push({
        key,
        texture,
        posType,
        opp,
        count,
        cbetPct: pct(count, opp),
        lowSample: opp < MIN_TEXTURE_SAMPLE,
      });
    }
  }
  return result;
}

// Fase B.2: builder para multiway. Solo 5 buckets (1 por textura, sin pos).
function buildCbetTextureMultiArray(
  cbetByTextureMulti: Partial<Record<CbetTextureMultiKey, CbetTextureStats>> | undefined
): CbetTextureMultiDerived[] {
  const result: CbetTextureMultiDerived[] = [];
  for (const texture of TEXTURE_ORDER) {
    const bucket = cbetByTextureMulti?.[texture];
    const opp = bucket?.opp || 0;
    const count = bucket?.count || 0;
    result.push({
      texture,
      opp,
      count,
      cbetPct: pct(count, opp),
      lowSample: opp < MIN_TEXTURE_SAMPLE,
    });
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// derivedStats — main entry point
// ─────────────────────────────────────────────────────────────────────────────
export function derivedStats(t: HistoryStats): DerivedStats {
  const afDenom = t.afCalls || 1;
  const probeOpp = (t.probeTurnOpp || 0) + (t.probeRiverOpp || 0);
  const probeCount = (t.probeTurnCount || 0) + (t.probeRiverCount || 0);

  // Fase C: Fold to Push por stack bin
  const foldToPushArray = buildFoldToPushArray(t.foldToPushByShoverStack);

  // VPIP por posición
  const vpipArray = buildVpipArray(t.vpipByPosition);
  const vpipTotalOpp = vpipArray.reduce((s, v) => s + v.opp, 0);

  // Fase 3: agregar el RFI total sumando todos los buckets
  const rfiArray = buildRfiArray(t.rfiByPosition);
  const rfiTotalOpp = rfiArray.reduce((s, r) => s + r.opp, 0);
  const rfiTotalCount = rfiArray.reduce((s, r) => s + r.count, 0);

  // Fase A: array de push/fold y total opps
  const pushFoldArray = buildPushFoldArray(t.pushFoldByPosition);
  const pushFoldTotalOpp = pushFoldArray.reduce((s, p) => s + p.opp, 0);

  // Fase B: array de cbet por textura × posición
  const cbetTextureArray = buildCbetTextureArray(t.cbetFlopByTexture);
  const cbetFlopByTextureTotalOpp = cbetTextureArray.reduce((s, c) => s + c.opp, 0);

  // Fase B.2: array de cbet por textura — multiway
  const cbetTextureMultiArray = buildCbetTextureMultiArray(t.cbetFlopByTextureMulti);
  const cbetFlopByTextureMultiTotalOpp = cbetTextureMultiArray.reduce((s, c) => s + c.opp, 0);

  const base: DerivedStats = {
    vpip: pct(t.vpipCount, t.handsPlayed),
    pfr: pct(t.pfrCount, t.handsPlayed),
    pfrVpipRatio: t.vpipCount > 0 ? Math.round((t.pfrCount / t.vpipCount) * 100) : 0,
    threebet: pct(t.threebetCount, t.threebetOpp),
    foldTo3bet: pct(t.foldTo3betCount, t.foldTo3betOpp),

    // Fase 2: splits — exponemos también los opp para que la UI pueda
    // decidir si mostrar el número o "insufficient sample"
    foldTo3betAllin: pct(t.foldTo3betAllinCount || 0, t.foldTo3betAllinOpp || 0),
    foldTo3betNonAllin: pct(t.foldTo3betNonAllinCount || 0, t.foldTo3betNonAllinOpp || 0),
    foldTo3betAllinOpp: t.foldTo3betAllinOpp || 0,
    foldTo3betNonAllinOpp: t.foldTo3betNonAllinOpp || 0,

    // Fase C: Fold to Push
    foldToPush: pct(t.foldToPushCount || 0, t.foldToPushOpp || 0),
    foldToPushOpp: t.foldToPushOpp || 0,
    foldToPushByShoverStack: foldToPushArray,

    // VPIP por posición
    vpipByPosition: vpipArray,
    vpipTotalOpp,

    // Fase 3: RFI por posición
    rfiByPosition: rfiArray,
    rfiTotal: pct(rfiTotalCount, rfiTotalOpp),
    rfiTotalOpp,

    // Fase A: Push/Fold short stack por posición
    pushFoldByPosition: pushFoldArray,
    pushFoldTotalOpp,

    // Fase B: CBet Flop por textura × posición
    cbetFlopByTexture: cbetTextureArray,
    cbetFlopByTextureTotalOpp,

    // Fase B.2: CBet Flop por textura — multiway
    cbetFlopByTextureMulti: cbetTextureMultiArray,
    cbetFlopByTextureMultiTotalOpp,

    ats: pct(t.atsCount, t.atsOpp),
    af: Math.round(((t.afBets + t.afRaises) / afDenom) * 100) / 100,
    afq: pct(
      t.afBets + t.afRaises,
      t.afBets + t.afRaises + t.afCalls + (t.afChecks || 0)
    ),

    cbetFlop: pct(t.cbetFlopCount, t.cbetFlopOpp),
    cbetFlopSuccess: pct(t.cbetFlopSuccessCount, t.cbetFlopCount),
    cbetTurn: pct(t.cbetTurnCount, t.cbetTurnOpp),
    cbetTurnSuccess: pct(t.cbetTurnSuccessCount, t.cbetTurnCount),
    cbetRiver: pct(t.cbetRiverCount, t.cbetRiverOpp),
    cbetRiverSuccess: pct(t.cbetRiverSuccessCount, t.cbetRiverCount),
    delayCbet: pct(t.delayCbetCount || 0, t.delayCbetOpp || 0),
    delayCbetOpp: t.delayCbetOpp || 0,
    cbet3BetPot: pct(t.cbet3BetPotCount || 0, t.cbet3BetPotOpp || 0),
    cbet3BetPotOpp: t.cbet3BetPotOpp || 0,
    cbet3BetPotIP: pct(t.cbet3BetPotIPCount || 0, t.cbet3BetPotIPOpp || 0),
    cbet3BetPotIPOpp: t.cbet3BetPotIPOpp || 0,
    cbet3BetPotOOP: pct(t.cbet3BetPotOOPCount || 0, t.cbet3BetPotOOPOpp || 0),
    cbet3BetPotOOPOpp: t.cbet3BetPotOOPOpp || 0,

    // Fase 1: matchup breakdown
    cbetFlopByMatchup: buildMatchupArray(t.cbetFlopMatchups),

    // Fase 4: matchup breakdowns del 3bet (hero como 3bettor) y fold-to-3bet (hero como opener)
    threebetByMatchup: buildMatchupArray(t.threebetByMatchup),
    foldTo3betByMatchup: buildMatchupArray(t.foldTo3betByMatchup),

    foldToCbetFlop: pct(t.foldToCbetFlopCount, t.foldToCbetFlopOpp),
    callCbetFlop: pct(t.callCbetFlopCount, t.foldToCbetFlopOpp),
    foldToCbetTurn: pct(t.foldToCbetTurnCount, t.foldToCbetTurnOpp),
    callCbetTurn: pct(t.callCbetTurnCount, t.foldToCbetTurnOpp),
    foldToCbetRiver: pct(t.foldToCbetRiverCount, t.foldToCbetRiverOpp),
    callCbetRiver: pct(t.callCbetRiverCount, t.foldToCbetRiverOpp),

    donkFlop: pct(t.donkFlopCount, t.donkFlopOpp),
    donkTurn: pct(t.donkTurnCount, t.donkTurnOpp),
    donkRiver: pct(t.donkRiverCount, t.donkRiverOpp),
    probeTurn: pct(t.probeTurnCount, t.probeTurnOpp),
    probeRiver: pct(t.probeRiverCount, t.probeRiverOpp),
    probeTotal: pct(probeCount, probeOpp),

    wtsd: pct(t.wtsdCount, t.flopsSeen),
    wwsf: pct(t.wwsfCount, t.wwsfOpp),
    wsd: pct(t.wsdCount, t.wsdOpp),
    positionBreakdown: { ...t.positionCounts },
  };

  const defendNum = (t.bbDefendCall || 0) + (t.bbDefendRaise || 0);
  base.bbDefendPct =
    t.bbDefendOpp && t.bbDefendOpp > 0 ? Math.round((defendNum / t.bbDefendOpp) * 100) : 0;

  base.cbetFlopDryPct =
    t.flopDryCount && t.flopDryCount > 0
      ? Math.round(((t.cbetFlopDryCount || 0) / t.flopDryCount) * 100)
      : 0;

  base.btnStealPct =
    t.btnStealOpp && t.btnStealOpp > 0
      ? Math.round(((t.btnStealCount || 0) / t.btnStealOpp) * 100)
      : 0;

  base.sbStealPct =
    t.sbStealOpp && t.sbStealOpp > 0
      ? Math.round(((t.sbStealCount || 0) / t.sbStealOpp) * 100)
      : 0;

  base.doubleBarrelPct =
    t.doubleBarrelOpp && t.doubleBarrelOpp > 0
      ? Math.round(((t.doubleBarrelCount || 0) / t.doubleBarrelOpp) * 100)
      : 0;

  // Squeeze
  base.squeezeOpp = t.squeezeOpp || 0;
  base.squeeze = base.squeezeOpp > 0
    ? pct(t.squeezeCount || 0, base.squeezeOpp)
    : 0;

  // 4bet
  base.fourbetOpp = t.fourbetOpp || 0;
  base.fourbet = base.fourbetOpp > 0
    ? pct(t.fourbetCount || 0, base.fourbetOpp)
    : 0;

  // Open Limp global (suma de limps por posición)
  const totalLimps = rfiArray.reduce((s, r) => s + r.limps, 0);
  base.openLimpOpp = rfiTotalOpp;
  base.openLimp = base.openLimpOpp > 0 ? pct(totalLimps, base.openLimpOpp) : 0;

  base.xraiseFlopOpp = t.xraiseFlopOpp || 0;
  base.xraiseFlopPct = pct(t.xraiseFlopCount || 0, t.xraiseFlopOpp || 0);
  base.xraiseFlopAsPFAOpp = t.xraiseFlopAsPFAOpp || 0;
  base.xraiseFlopAsPFAPct = pct(t.xraiseFlopAsPFACount || 0, t.xraiseFlopAsPFAOpp || 0);
  base.xraiseFlopNonPFAOpp = t.xraiseFlopNonPFAOpp || 0;
  base.xraiseFlopNonPFAPct = pct(t.xraiseFlopNonPFACount || 0, t.xraiseFlopNonPFAOpp || 0);

  return base;
}
